/**
 * Shared Expedition-related types + message constants (Round 6, plan-6 Phase DD).
 *
 * The Expedition is the v2 successor to v1's instanced dungeons (GDD §9 +
 * `docs/design/realms-and-expeditions.md`). At MVP a single Expedition exists
 * per realm — Verdana shallow — and players assemble at a persistent overworld
 * "door" structure, offer materials, and accrue ritual progress over real time
 * before the door opens for a 24-hour entry window (Phase EE wires the window).
 *
 * Phase DD owns the **opening ritual** message surface only:
 *   - `MSG_RITUAL_OFFER`   (client → server) — drop materials at the door.
 *   - `MSG_RITUAL_JOIN`    (client → server) — claim a party slot during accrual / window.
 *   - `MSG_RITUAL_STATE`   (server → client) — broadcast progress + party state.
 *
 * `MSG_RITUAL_ENTER` (window-close handoff) and the in-Expedition messages are
 * out of scope for Phase DD — see plan-6 §2 Phase EE and following.
 *
 * Pure module: no runtime dependencies beyond TypeScript. No I/O, no Colyseus,
 * no network imports. Safe to consume from either package (client/server).
 *
 * @packageDocumentation
 */
// ── Constants ─────────────────────────────────────────────────────────────
/**
 * Stable door identifier for the Verdana shallow Expedition (one-per-realm at
 * MVP). The string format is `expedition_door:<realmKey>_<depth>` and is the
 * primary key for the `opening_rituals` SQLite row.
 */
export const VERDANA_SHALLOW_DOOR_ID = 'expedition_door:verdana_shallow';
/**
 * Stable door identifier for the Sablewind shallow Expedition (round-7
 * follow-up — second realm reachable from the overworld). Same string format
 * as {@link VERDANA_SHALLOW_DOOR_ID}; primary key for its `opening_rituals`
 * SQLite row.
 */
export const SABLEWIND_SHALLOW_DOOR_ID = 'expedition_door:sablewind_shallow';
/**
 * Stable door identifier for the Frostmere shallow Expedition (round-7
 * follow-up — third realm reachable from the overworld). Same string format
 * as {@link VERDANA_SHALLOW_DOOR_ID}; primary key for its `opening_rituals`
 * SQLite row.
 */
export const FROSTMERE_SHALLOW_DOOR_ID = 'expedition_door:frostmere_shallow';
/**
 * Stable door identifier for the Ember Mire shallow Expedition (round-7
 * follow-up — fourth realm reachable from the overworld, completing the
 * 4-realm shallow coverage). Same string format as
 * {@link VERDANA_SHALLOW_DOOR_ID}; primary key for its `opening_rituals`
 * SQLite row.
 */
export const EMBER_MIRE_SHALLOW_DOOR_ID = 'expedition_door:ember_mire_shallow';
// ── Medium Expedition door IDs (Round 19 Phase A) ────────────────────────
export const VERDANA_MEDIUM_DOOR_ID = 'expedition_door:verdana_medium';
export const SABLEWIND_MEDIUM_DOOR_ID = 'expedition_door:sablewind_medium';
export const FROSTMERE_MEDIUM_DOOR_ID = 'expedition_door:frostmere_medium';
export const EMBER_MIRE_MEDIUM_DOOR_ID = 'expedition_door:ember_mire_medium';
// ── Deep Expedition door IDs (Round 19 Phase B) ─────────────────────────
export const VERDANA_DEEP_DOOR_ID = 'expedition_door:verdana_deep';
export const SABLEWIND_DEEP_DOOR_ID = 'expedition_door:sablewind_deep';
export const FROSTMERE_DEEP_DOOR_ID = 'expedition_door:frostmere_deep';
export const EMBER_MIRE_DEEP_DOOR_ID = 'expedition_door:ember_mire_deep';
export const EXPEDITION_TIER_CONFIGS = {
    shallow: {
        tier: 'shallow',
        windowDurationMs: 24 * 60 * 60 * 1000, // 24h
        windowDurationDevMs: 4 * 60 * 1000, // 4 min
        durationDays: 1,
        interiorWidth: 40,
        interiorHeight: 30,
        ritualChamberCount: 1,
        creatureCount: 3,
    },
    medium: {
        tier: 'medium',
        windowDurationMs: 24 * 60 * 60 * 1000, // 24h
        windowDurationDevMs: 4 * 60 * 1000, // 4 min
        durationDays: 3,
        interiorWidth: 80,
        interiorHeight: 60,
        ritualChamberCount: 3,
        creatureCount: 5,
    },
    deep: {
        tier: 'deep',
        windowDurationMs: 3 * 24 * 60 * 60 * 1000, // 3 days
        windowDurationDevMs: 12 * 60 * 1000, // 12 min
        durationDays: 7,
        interiorWidth: 120,
        interiorHeight: 90,
        ritualChamberCount: 5,
        creatureCount: 8,
    },
};
/**
 * Map a door ID to its expedition tier. Defaults to 'shallow' for unknown doors.
 */
export function getExpeditionTierForDoor(doorId) {
    if (doorId.includes('_medium'))
        return 'medium';
    if (doorId.includes('_deep'))
        return 'deep';
    return 'shallow';
}
// ── Messages ──────────────────────────────────────────────────────────────
/**
 * Client → server: a player drops a stack of one material at the door.
 *
 * The server validates the offering against the door's recipe (rejecting bad
 * materials with a soft system message — no penalty), and on accept records
 * the contribution against `offeringsAccepted[characterId][materialType]` and
 * accrues progress proportional to the recipe's progress weight for that
 * material. **Phase DD does NOT touch the offering player's inventory** — the
 * `LootSystem` / inventory integration is owned by Phase EE/GG. This message
 * carries the *intent*; the server returns what should be deducted.
 */
export const MSG_RITUAL_OFFER = 'ritual_offer';
/**
 * Client → server: claim a slot in the party that will enter the Expedition
 * once the entry window closes. Eligible while state is `'accruing'` or
 * `'open'`; rejected when full (cap 8) or when the ritual hasn't started yet.
 */
export const MSG_RITUAL_JOIN = 'ritual_join';
/**
 * Client → server: claim a slot in the Expedition party that will be moved
 * into the interior at window-close (Phase EE).
 *
 * Eligible while state is `'open'` and the entry window has not yet closed
 * (`now < windowClosesAt`). Rejected when full (cap 8) or when the ritual
 * isn't open. Idempotent — a re-enter by the same character returns
 * `joined: false` with reason `'already-joined'`.
 *
 * Distinct from {@link MSG_RITUAL_JOIN}: that message claims a slot during
 * accrual (so progress accrues toward this party's ritual). `MSG_RITUAL_ENTER`
 * specifically registers that the player is *physically present* at the
 * threshold and ready to be teleported into the Expedition when the window
 * closes. Today the server treats both messages identically (both call the
 * same `joinParty` path) — the split exists so the client can render two
 * separate affordances ("Join the ritual" vs "Step through when the door
 * opens") and a future iteration can require physical proximity for
 * `MSG_RITUAL_ENTER` while keeping `MSG_RITUAL_JOIN` available from anywhere
 * in the door's accrual zone.
 */
export const MSG_RITUAL_ENTER = 'ritual_enter';
/**
 * Server → client (broadcast to everyone in proximity, MVP: room-wide):
 * the door's ritual state changed. Delivered:
 *   - on every successful `MSG_RITUAL_OFFER`,
 *   - on every successful `MSG_RITUAL_JOIN`,
 *   - on internal state transitions (accruing → open, open → expired),
 *   - on a debounced cadence as `tickProgress` accrues progress (≥1 pct change).
 *
 * The client mirrors this onto a HUD panel beside the door; no other client
 * surface consumes it at MVP.
 */
export const MSG_RITUAL_STATE = 'ritual_state';
/**
 * Client → server: a player on the exit tile inside an Expedition is
 * voluntarily leaving (Phase HH). The party continues with remaining
 * members; if this is the last member the Expedition closes.
 *
 * **Phase FF placeholder.** The server-side trigger that fires this is wired
 * by Phase HH — Phase FF only registers the leaver-to-overworld zone flip
 * handler so the message can arrive once HH lands.
 */
export const MSG_EXPEDITION_LEAVE = 'expedition_leave';
// ── Round 6 Phase GG — camp-and-survive loop ──────────────────────────────
/**
 * Server → client (broadcast to every player whose `zone` is this
 * Expedition): the per-tick state snapshot for the camp HUD.
 *
 * Delivered on every tick that mutates `campHealth`, `suppliesRemaining`,
 * or the ritual chamber progress. The HUD displays campHealth + supplies
 * bars and a ritual-chamber progress meter when the local player is on
 * the pedestal.
 *
 * The payload is intentionally compact — the HUD doesn't need the full
 * `ExpeditionState` row, just the moving numbers. The `chamberPct`
 * field is null when no party member is currently advancing the
 * chamber timer.
 */
export const MSG_EXPEDITION_STATE = 'expedition_state';
/**
 * Client → server: the local player is interacting with the ritual
 * chamber pedestal. Sent each tick the player remains on the pedestal —
 * the server uses repeated sends to advance a continuous timer.
 *
 * Phase GG MVP: a single chamber per Expedition; the `chamberId` field
 * exists for forward-compat with future multi-chamber Expeditions but
 * defaults to `chamber:<expeditionId>` on the server.
 */
export const MSG_EXPEDITION_RITUAL_CHAMBER_INTERACT = 'expedition_ritual_chamber_interact';
/**
 * Server → client (broadcast to every party member): the ritual chamber
 * has reached 100% — the Expedition is "ready for exit" (Phase HH wires
 * the actual exit + reward path).
 */
export const MSG_EXPEDITION_RITUAL_CHAMBER_COMPLETE = 'expedition_ritual_chamber_complete';
// ── Round 6 Phase HH — Exit + reward ──────────────────────────────────────
/**
 * Server → client (per-recipient): the ritual-complete exit path fired and
 * the player has been granted the Expedition's reward bundle. Sent once
 * per party member at exit-time, alongside the `MSG_TILEMAP` push that
 * returns them to the overworld.
 *
 * Payload carries:
 *   - `expeditionId` — which Expedition just closed.
 *   - `items` — the placeholder Relic + journal entry (today both are
 *     `MaterialWood` until the round 7+ Relic + Journal systems land;
 *     `itemNames` carries the raw `ItemType` enum string so the client
 *     can map to a display label via `itemTypeFromName` — same shape as
 *     `LootPickupEvent`. The array is intentionally empty for the
 *     voluntary-leave path; only ritual-complete exits grant a reward).
 *   - `remembrance` — the narrative line written to the village_board
 *     row. The MVP client logs it + flashes a brief on-screen notice;
 *     round 7+ wires the journal display surface.
 *
 * Phase HH MVP: no per-realm Relic catalog, no journal UI, no
 * village-board UI. The event exists so the client can confirm receipt
 * and surface a soft notice; round 7+ broadens the payload + the UI.
 */
export const MSG_EXPEDITION_REWARD_GRANTED = 'expedition_reward_granted';
// ── Round 17 Phase D — Boulder puzzle ─────────────────────────────────────
/**
 * Client → server: the local player is pushing a boulder.
 */
export const MSG_BOULDER_PUSH = 'boulder_push';
/**
 * Server → client (broadcast to zone): boulder event notification.
 */
export const MSG_BOULDER_EVENT = 'boulder_event';
/** Time (seconds) a solo player must push to move a boulder one tile. */
export const BOULDER_PUSH_DURATION_S = 2.5;
/** Co-op speed multiplier with 2 pushers. */
export const BOULDER_COOP_2P_MULT = 1.5;
/** Co-op speed multiplier with 3+ pushers. */
export const BOULDER_COOP_3P_MULT = 2.0;
/** Default gravity speed in tiles per second. */
export const BOULDER_GRAVITY_SPEED_TPS = 0.4;
/** Max pixel distance from boulder to count as adjacent for push. */
export const BOULDER_PUSH_ADJACENCY_PX = 40;
/** Delay (ms) before a boulder resets to spawn position. */
export const BOULDER_RESET_DELAY_MS = 2500;
// ── Round 19 Phase D — Supplies regeneration via party rituals ───────────
/**
 * Supplies percentage restored when a cooked food item is consumed at
 * the expedition camp. A single food item is a modest restore; the Cook
 * vocation's witness multiplier applies additively.
 */
export const EXPEDITION_COOK_SUPPLIES_RESTORE_PCT = 5;
/**
 * Supplies percentage restored by the Long Meal expedition ritual.
 * Significantly larger than single-food cooking — requires a Cook
 * vocation party member to initiate.
 */
export const EXPEDITION_LONG_MEAL_SUPPLIES_RESTORE_PCT = 20;
/**
 * Supplies percentage restored per successful gathering event inside
 * an Expedition. Small drip-feed from camp-side foraging.
 */
export const EXPEDITION_GATHER_SUPPLIES_RESTORE_PCT = 3;
/**
 * Warden vocation decay slowdown multiplier. Applied on top of the
 * existing "parked at camp" half-rate. With a Warden present, camp
 * health and supplies decay at 30% of the base un-parked rate
 * (vs 50% for non-Warden parked).
 */
export const WARDEN_EXPEDITION_DECAY_MULTIPLIER = 0.3;
/**
 * Duration of the expedition Long Meal ritual in real seconds.
 * 2 minutes — shorter than the home-hearth Long Meal (3 min) because
 * Expedition pacing is faster.
 */
export const EXPEDITION_LONG_MEAL_DURATION_S = 120;
/**
 * Client → server: a Cook character initiates a Long Meal at the
 * expedition camp, consuming one food item and restoring party supplies.
 * Requires the initiator to have Cook vocation. Only valid inside an
 * Expedition zone.
 */
export const MSG_EXPEDITION_LONG_MEAL = 'expedition_long_meal';
/**
 * Server → client: Long Meal result broadcast to all party members
 * in the Expedition zone.
 */
export const MSG_EXPEDITION_LONG_MEAL_RESULT = 'expedition_long_meal_result';
/**
 * Server → client: rejection of a Long Meal request.
 */
export const MSG_EXPEDITION_LONG_MEAL_REJECTED = 'expedition_long_meal_rejected';
/**
 * Client → server: consume a food item at the expedition camp to
 * restore a small amount of supplies. Any party member can do this;
 * Cook vocation grants a bonus. Only valid inside an Expedition zone.
 */
export const MSG_EXPEDITION_COOK_CONSUME = 'expedition_cook_consume';
/**
 * Server → client: food consumed at camp, supplies partially restored.
 */
export const MSG_EXPEDITION_COOK_RESULT = 'expedition_cook_result';
// ── Zone-prefix doc reminder ──────────────────────────────────────────────
//
// `PlayerEntity.zone` (server-side schema) accepts the following prefixes:
//   - `'overworld'`           — default; the open world.
//   - `'dungeon:<theme>'`     — V-3 in-world dungeon zone (e.g. `dungeon:cave`).
//   - `'home:<ownerId>'`      — V-3 in-world home zone.
//   - `'expedition:<id>'`     — Round 6 Phase FF. `<id>` is the Expedition's
//                               stable `expeditionId`. Players whose `zone`
//                               starts with this prefix are inside an
//                               Expedition; on relogin they are restored to
//                               the matching Expedition zone if still a
//                               member of that Expedition.
//
// The client-side prefix helpers (`isDungeonZone`, `isHomeZone`,
// `isExpeditionZone`) live in `packages/client/src/scenes/portalTransition.ts`.
//# sourceMappingURL=expedition.js.map